/**
 * Message Input Component
 * Input area for sending messages with file upload support
 */

import { useState, useRef, useEffect } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSendMessage } from '@/hooks/useMessages';
import { useAppStore } from '@/store/app.store';
import { getValidationError } from '@/utils/file-validation.util';
import { uploadWithRetry } from '@/services/attachments.service';
import type { PaginatedMessagesResponse } from '@/services/messages.service';
import socketClient from '@/services/socket.client';
import { apiClient } from '@/services/api.client';
import FileUploadButton from '@/components/attachments/FileUploadButton';
import FilePreviewList from '@/components/attachments/FilePreviewList';
import { PollCreationModal } from '@/components/polls';
import { VoiceRecordingInline } from '@/components/voice-messages';
import { MentionAutocomplete, type MentionSelection } from '@/components/mentions/MentionAutocomplete';
import { ChannelMentionAutocomplete, type ChannelSelection } from '@/components/mentions/ChannelMentionAutocomplete';
import { useChannels } from '@/hooks/useChannels';
import { serializeChannelReferences, stripMentionHtml } from '@/utils/linkify.util';
import { SlashCommandAutocomplete } from '@/components/messages/SlashCommandAutocomplete';
import { listSlashCommands, executeSlashCommand, type SlashCommand } from '@/services/slash-command.service';
import ScheduleMessageModal from '@/components/messages/ScheduleMessageModal';
import ScheduledMessagesList from '@/components/messages/ScheduledMessagesList';
import { GifPicker, type GifItem, gifService } from '@/components/gifs';
import EmojiPicker from '@/components/reactions/EmojiPicker';
import type { FileUploadState, Message, MessageAttachment, SendOption, User, PartialUser } from '@/types/api.types';
import { MESSAGE_MAX_LENGTH } from '@highfive/types';
import { isCompressibleMedia } from '@/utils/media-metadata.util';
import { shouldConvertPasteToFile, createTextFileFromPaste } from '@/utils/paste-as-file.util';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useUserSettings } from '@/hooks/useUserSettings';
import type { KeyboardShortcut } from '@/hooks/useKeyboardShortcuts';

// Global upload tracker to prevent duplicates across React StrictMode re-renders
type InfiniteMessagesData = InfiniteData<PaginatedMessagesResponse>;
let globalUploadInProgress = false;

interface MessageInputProps {
  replyToMessage?: Message | null;
  onCancelReply?: () => void;
  externalFiles?: File[];
  externalSendOption?: 'image' | 'file';
  onExternalFilesHandled?: () => void;
}

const MessageInput = ({ replyToMessage, onCancelReply, externalFiles, externalSendOption, onExternalFilesHandled }: MessageInputProps) => {
  const [content, setContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<FileUploadState[]>([]);
  const [showPollModal, setShowPollModal] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [pendingPollMessageId, setPendingPollMessageId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showScheduledList, setShowScheduledList] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(0);
  const [showChannelAutocomplete, setShowChannelAutocomplete] = useState(false);
  const [channelQuery, setChannelQuery] = useState('');
  const [channelStartIndex, setChannelStartIndex] = useState(0);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [showSlashAutocomplete, setShowSlashAutocomplete] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [availableCommands, setAvailableCommands] = useState<SlashCommand[]>([]);
  // Maps "@DisplayName" → userId for reconstructing mention HTML on send
  const mentionMapRef = useRef<Map<string, string>>(new Map());
  // Maps "#ChannelName" → channelId for reconstructing channel HTML on send
  const channelMapRef = useRef<Map<string, string>>(new Map());
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const gifButtonRef = useRef<HTMLButtonElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPasteFileIdRef = useRef<string | null>(null);
  const preAcquiredStreamRef = useRef<MediaStream | null>(null);
  const { activeChannelId, activeDMThreadId, setDraft, clearDraft } = useAppStore();
  const conversationId = activeChannelId || activeDMThreadId;
  const sendMessageMutation = useSendMessage();
  const queryClient = useQueryClient();
  const { data: channels } = useChannels();
  const referenceableChannels = (channels ?? []).filter((c) => !c.is_archived);
  const { data: userSettings } = useUserSettings();
  const pasteThreshold = userSettings?.paste_as_file_threshold ?? 200;

  // Fetch available users for mentions (channel members or DM participants + bots)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        let users: User[] = [];

        if (activeChannelId) {
          // Fetch channel members
          const response = await apiClient.get(`/channels/${activeChannelId}/members`);
          if (response.data.status === 'success' && response.data.data?.members) {
            users = response.data.data.members.map((m: { users: User }) => m.users);
          }
        } else if (activeDMThreadId) {
          // For DMs, fetch all participants
          const response = await apiClient.get(`/dm-threads/${activeDMThreadId}`);
          if (response.data.data?.thread?.members) {
            users = response.data.data.thread.members;
          } else if (response.data.data?.other_user) {
            users = [response.data.data.other_user];
          }
        }

        // Also fetch enabled assistants and merge (avoid duplicates)
        try {
          const assistantsResponse = await apiClient.get('/assistants');
          if (assistantsResponse.data.data?.assistants) {
            const assistantUsers: User[] = assistantsResponse.data.data.assistants
              .map((a: { users_assistant_account?: PartialUser }) => a.users_assistant_account)
              .filter((acc: PartialUser | undefined): acc is PartialUser => Boolean(acc))
              .map((u: PartialUser) => ({ ...u, is_bot: true } as unknown as User));
            const existingIds = new Set(users.map((u) => u.id));
            for (const assistantUser of assistantUsers) {
              if (!existingIds.has(assistantUser.id)) {
                users.push(assistantUser);
              }
            }
          }
        } catch {
          // Assistants fetch is non-critical
        }

        setAvailableUsers(users);
      } catch (error) {
        console.error('Failed to fetch users for mentions:', error);
      }
    };

    fetchUsers();
  }, [activeChannelId, activeDMThreadId]);

  // Fetch available slash commands once (role-filtered by the backend)
  useEffect(() => {
    listSlashCommands()
      .then(setAvailableCommands)
      .catch(() => {
        // Non-critical — autocomplete simply won't show
      });
  }, []);

  // Restore the saved draft for this conversation when switching channels/DMs.
  // Reads directly from the store to avoid re-subscribing on every draft change.
  useEffect(() => {
    const draft = conversationId
      ? useAppStore.getState().drafts[conversationId] ?? ''
      : '';
    setContent(draft);
  }, [conversationId]);

  // Auto-focus textarea when navigating to a channel or DM
  useEffect(() => {
    const timer = setTimeout(() => {
      textareaRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [activeChannelId, activeDMThreadId]);

  // Auto-focus textarea when replying to a message
  useEffect(() => {
    if (replyToMessage) {
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [replyToMessage]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [content]);

  // Typing detection
  const handleTypingDetection = () => {
    const conversationId = activeChannelId || activeDMThreadId;
    if (!conversationId) return;

    // Start typing on first keystroke
    if (!isTyping) {
      socketClient.sendTyping(activeChannelId ?? undefined, activeDMThreadId ?? undefined);
      setIsTyping(true);
    }

    // Reset inactivity timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 500ms inactivity
    typingTimeoutRef.current = setTimeout(() => {
      socketClient.sendStopTyping(activeChannelId ?? undefined, activeDMThreadId ?? undefined);
      setIsTyping(false);
    }, 500);
  };

  // Handle content change with typing detection and @ mention detection
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    const cursorPosition = e.target.selectionStart;

    setContent(newContent);
    handleTypingDetection();

    // Persist unsent draft per conversation (survives switching + reload)
    if (conversationId) {
      if (newContent) {
        setDraft(conversationId, newContent);
      } else {
        clearDraft(conversationId);
      }
    }

    // Detect "/" slash command trigger (only at the very start, before any space)
    if (newContent.startsWith('/')) {
      const firstSpace = newContent.indexOf(' ');
      if (firstSpace === -1) {
        setSlashQuery(newContent.slice(1));
        setShowSlashAutocomplete(true);
      } else {
        setShowSlashAutocomplete(false);
      }
    } else {
      setShowSlashAutocomplete(false);
    }

    // Detect @ mention trigger
    const textBeforeCursor = newContent.substring(0, cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterAt = atIndex !== -1 ? textBeforeCursor.substring(atIndex + 1) : '';
    // No space/newline between @ and the cursor
    const mentionActive =
      atIndex !== -1 && !textAfterAt.includes(' ') && !textAfterAt.includes('\n');

    // Detect # channel reference trigger (only at start of message or after whitespace,
    // so mid-word # like "тест#абв" or URLs with anchors don't trigger it)
    const hashIndex = textBeforeCursor.lastIndexOf('#');
    const charBeforeHash = hashIndex > 0 ? textBeforeCursor[hashIndex - 1] : '';
    const textAfterHash = hashIndex !== -1 ? textBeforeCursor.substring(hashIndex + 1) : '';
    const channelActive =
      hashIndex !== -1 &&
      (hashIndex === 0 || /\s/.test(charBeforeHash)) &&
      !textAfterHash.includes(' ') &&
      !textAfterHash.includes('\n');

    // When both triggers match (e.g. "#@abc"), the one closest to the cursor wins
    if (mentionActive && (!channelActive || atIndex > hashIndex)) {
      setMentionQuery(textAfterAt);
      setMentionStartIndex(atIndex);
      setShowMentionAutocomplete(true);
      setShowChannelAutocomplete(false);
    } else if (channelActive) {
      setChannelQuery(textAfterHash);
      setChannelStartIndex(hashIndex);
      setShowChannelAutocomplete(true);
      setShowMentionAutocomplete(false);
    } else {
      setShowMentionAutocomplete(false);
      setShowChannelAutocomplete(false);
    }
  };

  // Handle mention selection — insert plain "@DisplayName" into textarea, track in map
  const handleMentionSelect = (mention: MentionSelection) => {
    if (!textareaRef.current) return;

    const displayText = `@${mention.displayName}`;

    // Special mentions (@everyone, @here) are inserted as plain text — backend parses them
    // Regular user mentions are tracked in mentionMapRef for HTML reconstruction on send
    if (mention.userId) {
      mentionMapRef.current.set(displayText, mention.userId);
    }

    // Replace @query with plain display text
    const beforeMention = content.substring(0, mentionStartIndex);
    const afterMention = content.substring(textareaRef.current.selectionStart);
    const newContent = beforeMention + displayText + ' ' + afterMention;

    setContent(newContent);
    setShowMentionAutocomplete(false);

    // Set cursor position after mention
    setTimeout(() => {
      if (textareaRef.current) {
        const cursorPosition = beforeMention.length + displayText.length + 1;
        textareaRef.current.selectionStart = cursorPosition;
        textareaRef.current.selectionEnd = cursorPosition;
        textareaRef.current.focus();
      }
    }, 0);
  };

  // Handle closing autocomplete
  const handleMentionClose = () => {
    setShowMentionAutocomplete(false);
    setMentionQuery('');
  };

  // Handle channel reference selection — insert plain "#ChannelName" into textarea, track in map
  const handleChannelSelect = (channel: ChannelSelection) => {
    if (!textareaRef.current) return;

    const displayText = `#${channel.name}`;
    channelMapRef.current.set(displayText, channel.channelId);

    // Replace #query with plain display text
    const beforeChannel = content.substring(0, channelStartIndex);
    const afterChannel = content.substring(textareaRef.current.selectionStart);
    const newContent = beforeChannel + displayText + ' ' + afterChannel;

    setContent(newContent);
    setShowChannelAutocomplete(false);

    // Set cursor position after channel reference
    setTimeout(() => {
      if (textareaRef.current) {
        const cursorPosition = beforeChannel.length + displayText.length + 1;
        textareaRef.current.selectionStart = cursorPosition;
        textareaRef.current.selectionEnd = cursorPosition;
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleChannelClose = () => {
    setShowChannelAutocomplete(false);
    setChannelQuery('');
  };

  // Handle slash command selection — insert "/name " so the user can add arguments
  const handleSlashSelect = (command: SlashCommand) => {
    const newContent = `/${command.name} `;
    setContent(newContent);
    setShowSlashAutocomplete(false);
    setSlashQuery('');
    if (conversationId) setDraft(conversationId, newContent);
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = newContent.length;
        textareaRef.current.selectionStart = pos;
        textareaRef.current.selectionEnd = pos;
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleSlashClose = () => {
    setShowSlashAutocomplete(false);
    setSlashQuery('');
  };

  // Execute a slash command via the backend and act on the result
  const handleSlashCommand = async (commandContent: string) => {
    // Stop typing indicator
    if (isTyping) {
      socketClient.sendStopTyping(activeChannelId ?? undefined, activeDMThreadId ?? undefined);
      setIsTyping(false);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }
    setShowSlashAutocomplete(false);

    try {
      const result = await executeSlashCommand(commandContent, {
        channelId: activeChannelId || undefined,
        dmThreadId: activeDMThreadId || undefined,
      });

      // Clear the composer
      setContent('');
      if (conversationId) clearDraft(conversationId);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';

      if (result.action === 'replace' && result.modifiedContent) {
        // Send the transformed content as a normal message
        await sendMessageMutation.mutateAsync({
          content: result.modifiedContent,
          channelId: activeChannelId || undefined,
          dmThreadId: activeDMThreadId || undefined,
          replyToMessageId: replyToMessage?.id,
        });
        onCancelReply?.();
      } else if (result.message) {
        // Ephemeral feedback (help/status/mute/errors) — only visible to the sender
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      }
    } catch (error) {
      console.error('Slash command failed:', error);
      toast.error('Грешка при изпълнение на командата');
    }
  };

  // Apply text formatting (bold, italic, code)
  const applyFormatting = (format: 'bold' | 'italic' | 'code') => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    let formattedText = '';
    let cursorOffset = 0;

    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        cursorOffset = selectedText ? 2 : 2; // Position cursor inside **|**
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        cursorOffset = selectedText ? 1 : 1; // Position cursor inside *|*
        break;
      case 'code':
        formattedText = `\`${selectedText}\``;
        cursorOffset = selectedText ? 1 : 1; // Position cursor inside `|`
        break;
    }

    const newContent = content.substring(0, start) + formattedText + content.substring(end);
    setContent(newContent);

    // Set cursor position after formatting markers if no selection, or after formatted text if there was selection
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = selectedText
          ? start + formattedText.length
          : start + cursorOffset;
        textareaRef.current.selectionStart = newCursorPos;
        textareaRef.current.selectionEnd = newCursorPos;
        textareaRef.current.focus();
      }
    }, 0);
  };
  // Insert an emoji at the current cursor position in the textarea
  const insertEmoji = (emoji: string) => {
    const textarea = textareaRef.current;
    const start = textarea ? textarea.selectionStart : content.length;
    const end = textarea ? textarea.selectionEnd : content.length;

    const newContent = content.substring(0, start) + emoji + content.substring(end);
    setContent(newContent);

    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = start + emoji.length;
        textareaRef.current.selectionStart = newCursorPos;
        textareaRef.current.selectionEnd = newCursorPos;
        textareaRef.current.focus();
      }
    }, 0);
  };

  // Define message formatting keyboard shortcuts
  const messageShortcuts: KeyboardShortcut[] = [
    {
      key: 'b',
      ctrl: true,
      action: () => applyFormatting('bold'),
      description: 'Удебелен текст',
      category: 'formatting',
    },
    {
      key: 'i',
      ctrl: true,
      action: () => applyFormatting('italic'),
      description: 'Курсив текст',
      category: 'formatting',
    },
    {
      key: 'e',
      ctrl: true,
      action: () => applyFormatting('code'),
      description: 'Код форматиране',
      category: 'formatting',
    },
  ];

  // Register keyboard shortcuts
  useKeyboardShortcuts({
    enabled: true,
    shortcuts: messageShortcuts,
  });

  // Handle blur - stop typing
  const handleBlur = () => {
    if (isTyping) {
      socketClient.sendStopTyping(activeChannelId ?? undefined, activeDMThreadId ?? undefined);
      setIsTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (isTyping) {
        socketClient.sendStopTyping(activeChannelId ?? undefined, activeDMThreadId ?? undefined);
      }
    };
  }, [isTyping, activeChannelId, activeDMThreadId]);

  // Handle file selection — media files default to inline preview, others to file download
  const handleFilesSelected = (files: File[]) => {
    const newFileStates: FileUploadState[] = files.map((file) => ({
      file,
      id: `upload-${Date.now()}-${Math.random()}`,
      status: 'pending',
      progress: 0,
      error: getValidationError(file) || undefined,
      sendOption: isCompressibleMedia(file) ? 'image' : 'file',
    }));

    setSelectedFiles((prev) => [...prev, ...newFileStates]);
  };

  // Remove file from selection
  const handleRemoveFile = (fileId: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // Handle files dropped from DropZoneOverlay (via MessageArea)
  useEffect(() => {
    if (externalFiles && externalFiles.length > 0) {
      const newFileStates: FileUploadState[] = externalFiles.map((file) => ({
        file,
        id: `upload-${Date.now()}-${Math.random()}`,
        status: 'pending' as const,
        progress: 0,
        error: getValidationError(file) || undefined,
        sendOption: (externalSendOption || 'file') as SendOption,
      }));
      setSelectedFiles((prev) => [...prev, ...newFileStates]);
      onExternalFilesHandled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFiles]);

  // Paste handler — large text pastes become .txt file attachments
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Skip if clipboard contains files (e.g. pasted screenshot)
    if (e.clipboardData.files.length > 0) return;

    const pastedText = e.clipboardData.getData('text/plain');
    if (!pastedText) return;

    if (shouldConvertPasteToFile(pastedText, content, MESSAGE_MAX_LENGTH, pasteThreshold)) {
      e.preventDefault();

      const txtFile = createTextFileFromPaste(pastedText);
      const fileId = `upload-${Date.now()}-${Math.random()}`;
      lastPasteFileIdRef.current = fileId;

      const newFileState: FileUploadState = {
        file: txtFile,
        id: fileId,
        status: 'pending',
        progress: 0,
        error: getValidationError(txtFile) || undefined,
        sendOption: 'file',
      };
      setSelectedFiles((prev) => [...prev, newFileState]);

      toast('Текстът е добавен като файл', {
        action: {
          label: 'Постави като текст',
          onClick: () => {
            const idToRemove = lastPasteFileIdRef.current;
            if (idToRemove) {
              setSelectedFiles((prev) => prev.filter((f) => f.id !== idToRemove));
              lastPasteFileIdRef.current = null;
            }
            setContent((prev) => (prev + pastedText).substring(0, MESSAGE_MAX_LENGTH));
            textareaRef.current?.focus();
          },
        },
        duration: 6000,
      });
    }
    // Small pastes: native behavior (text goes inline)
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if there's content or valid files
    const hasContent = content.trim();
    const validFiles = selectedFiles.filter((f) => !f.error);
    const hasValidFiles = validFiles.length > 0;

    if (!hasContent && !hasValidFiles) return;

    // Slash command — content starting with "/" and no attachments is executed, not sent verbatim
    if (hasContent && content.trim().startsWith('/') && !hasValidFiles) {
      await handleSlashCommand(content.trim());
      return;
    }

    // Reconstruct mention HTML tags from the plain @DisplayName text
    let finalContent = content.trim();
    for (const [displayText, userId] of mentionMapRef.current.entries()) {
      if (finalContent.includes(displayText)) {
        finalContent = finalContent.replaceAll(
          displayText,
          `<mention data-user-id="${userId}">${displayText}</mention>`
        );
      }
    }

    // Reconstruct channel HTML tags from the plain #ChannelName text.
    // Single-pass regex (longest name first) so one channel name being a prefix
    // of another can't corrupt an already-inserted tag.
    finalContent = serializeChannelReferences(finalContent, channelMapRef.current);

    const messageData = {
      content: finalContent,
      channelId: activeChannelId || undefined,
      dmThreadId: activeDMThreadId || undefined,
      replyToMessageId: replyToMessage?.id,
      hasAttachments: hasValidFiles || undefined,
    };

    // Stop typing immediately when sending
    if (isTyping) {
      socketClient.sendStopTyping(activeChannelId ?? undefined, activeDMThreadId ?? undefined);
      setIsTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }

    try {
      // Send message first (optimistic)
      const newMessage = await sendMessageMutation.mutateAsync(messageData);

      // Clear content, mention/channel maps, draft, and reply immediately
      setContent('');
      if (conversationId) clearDraft(conversationId);
      mentionMapRef.current.clear();
      channelMapRef.current.clear();
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // Clear reply mode
      onCancelReply?.();

      // Upload files if any
      if (hasValidFiles) {
        // Update file states to 'uploading' with initial progress
        setSelectedFiles((prev) =>
          prev.map((f) =>
            f.error ? f : { ...f, status: 'uploading' as const, progress: 5 }
          )
        );

        // Upload files with progress tracking
        const conversationKey = activeChannelId || activeDMThreadId;
        const messagesQueryKey = ['messages', 'infinite', conversationKey];

        const uploadPromises = validFiles.map(async (fileState) => {
          try {
            const attachment = await uploadWithRetry(
              fileState.file,
              newMessage.id,
              (progress) => {
                setSelectedFiles((prev) =>
                  prev.map((f) =>
                    f.id === fileState.id ? { ...f, progress } : f
                  )
                );
              },
              3, // maxRetries
              false, // compress flag (not used for image/file distinction)
              fileState.sendOption // displayType: 'image' or 'file'
            );

            // Patch the cached message to include the new attachment
            // (WebSocket message:updated may also do this, but direct patch is more reliable)
            queryClient.setQueryData<InfiniteMessagesData>(messagesQueryKey, (old) => {
              if (!old?.pages?.length) return old;
              return {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  messages: page.messages.map((msg) => {
                    if (msg.id !== newMessage.id) return msg;
                    return {
                      ...msg,
                      message_attachments: [
                        ...(msg.message_attachments || []),
                        {
                          ...attachment,
                          file_path: attachment.storage_path,
                          uploaded_at: attachment.created_at,
                        } as MessageAttachment,
                      ],
                    };
                  }),
                })),
              };
            });

            // Mark as completed
            setSelectedFiles((prev) =>
              prev.map((f) =>
                f.id === fileState.id
                  ? { ...f, status: 'completed' as const, progress: 100 }
                  : f
              )
            );
          } catch (error) {
            // Mark as error
            setSelectedFiles((prev) =>
              prev.map((f) =>
                f.id === fileState.id
                  ? {
                      ...f,
                      status: 'error' as const,
                      error: 'Грешка при качване. Опитай отново.',
                    }
                  : f
              )
            );
            console.error('Failed to upload file:', error);
            toast.error(`Грешка при качване на ${fileState.file.name}`);
          }
        });

        // Wait for all uploads to complete/fail
        await Promise.allSettled(uploadPromises);

        // Clear completed files after short delay
        setTimeout(() => {
          setSelectedFiles((prev) =>
            prev.filter((f) => f.status !== 'completed')
          );
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Грешка при изпращане на съобщението');

      // Mark all pending/uploading files as errored so they don't stay stuck at 0%
      setSelectedFiles((prev) =>
        prev.map((f) =>
          f.status === 'pending' || f.status === 'uploading'
            ? {
                ...f,
                status: 'error' as const,
                error: 'Съобщението не беше изпратено. Опитай отново.',
              }
            : f
        )
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // While the slash-command dropdown is open, let it handle Enter/Tab (selection)
    if (showSlashAutocomplete && (e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
      e.preventDefault();
      return;
    }
    // While the channel dropdown is open, let it handle Enter/Tab (selection)
    if (showChannelAutocomplete && (e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
      e.preventDefault();
      return;
    }
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Handle poll creation
  const handleCreatePoll = async () => {
    // Create a placeholder message first
    const messageData = {
      content: '',
      channelId: activeChannelId || undefined,
      dmThreadId: activeDMThreadId || undefined,
      isPollPlaceholder: true,
    };

    try {
      const newMessage = await sendMessageMutation.mutateAsync(messageData);
      setPendingPollMessageId(newMessage.id);
      setShowPollModal(true);
    } catch (error) {
      console.error('Failed to create message for poll:', error);
      toast.error('Грешка при създаване на анкета');
    }
  };

  const handlePollCreated = () => {
    setPendingPollMessageId(null);
    setShowPollModal(false);
  };

  const handlePollCancelled = () => {
    setPendingPollMessageId(null);
    setShowPollModal(false);
  };

  // Handle voice message recording
  const handleVoiceMessageSend = async (audioFile: File) => {
    // Prevent duplicate uploads in React StrictMode (development double-invoke)
    if (globalUploadInProgress) {
      console.log('[Voice] Upload already in progress, skipping duplicate');
      return;
    }

    globalUploadInProgress = true;
    setIsRecordingVoice(false);

    const messageData = {
      content: '',
      channelId: activeChannelId || undefined,
      dmThreadId: activeDMThreadId || undefined,
      isVoiceMessage: true,
    };

    try {
      const newMessage = await sendMessageMutation.mutateAsync(messageData);

      // Upload audio file — the backend broadcasts `message:updated` via
      // WebSocket after saving the attachment, which updates all clients'
      // caches (including the sender's) with the full message + attachments.
      await uploadWithRetry(audioFile, newMessage.id);

      console.log('[Voice] Upload successful:', newMessage.id);

    } catch (error) {
      console.error('Failed to send voice message:', error);
      toast.error('Грешка при изпращане на гласово съобщение');
    } finally {
      globalUploadInProgress = false;
    }
  };

  // Handle GIF selection - send as message with GIF URL
  const handleGifSelect = async (gif: GifItem) => {
    const gifUrl = gifService.getBestFormat(gif);

    const messageData = {
      content: gifUrl, // Send GIF URL as message content
      channelId: activeChannelId || undefined,
      dmThreadId: activeDMThreadId || undefined,
      replyToMessageId: replyToMessage?.id,
    };

    try {
      await sendMessageMutation.mutateAsync(messageData);
      onCancelReply?.();
    } catch (error) {
      console.error('Failed to send GIF:', error);
      toast.error('Грешка при изпращане на GIF');
    }
  };

  const canSend =
    content.trim() || selectedFiles.some((f) => !f.error);

  return (
    <div className="relative rounded-xl transition-all">
      <form onSubmit={handleSubmit} className="relative">
        {/* Reply Context */}
        {replyToMessage && (
          <div className="mb-3 px-4 py-3 bg-gray-50 dark:bg-dark-bg-tertiary border-l-4 border-blue-500 rounded-r-lg flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                  />
                </svg>
                <span className="text-xs font-medium text-gray-700 dark:text-dark-text-secondary">
                  Отговаряш на {replyToMessage.users.display_name}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-dark-text-muted truncate">
                {stripMentionHtml(replyToMessage.content)}
              </p>
            </div>
            <button
              type="button"
              onClick={onCancelReply}
              className="ml-2 p-1 text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary rounded transition-colors"
              title="Откажи отговор"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* File previews */}
        <FilePreviewList
          files={selectedFiles}
          onRemove={handleRemoveFile}
        />

        {/* Voice Recording Inline (replaces input when recording) */}
        {isRecordingVoice ? (
          <VoiceRecordingInline
            onCancel={() => {
              // Clean up pre-acquired stream if recording is cancelled before use
              if (preAcquiredStreamRef.current) {
                preAcquiredStreamRef.current.getTracks().forEach(t => t.stop());
                preAcquiredStreamRef.current = null;
              }
              setIsRecordingVoice(false);
            }}
            onSend={(audioFile) => {
              preAcquiredStreamRef.current = null;
              handleVoiceMessageSend(audioFile);
            }}
            preAcquiredStream={preAcquiredStreamRef.current || undefined}
          />
        ) : (
          /* Discord-style Input Container */
          <div className="bg-dark-bg-input dark:bg-dark-bg-input rounded-xl border border-dark-border-secondary/30 flex items-end transition-colors focus-within:border-primary-500/40 focus-within:shadow-[0_0_0_2px_rgba(51,102,255,0.1)]">
            {/* Left side buttons */}
            <div className="flex items-center px-2 py-2 gap-0.5">
              {/* File upload button */}
              <FileUploadButton
                onFilesSelected={handleFilesSelected}
                disabled={sendMessageMutation.isPending}
              />
            </div>

            {/* Textarea container */}
            <div className="flex-1 relative py-2">
              <textarea
                ref={textareaRef}
                value={content}
                onChange={handleContentChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder="Въведете съобщение"
                className="w-full px-2 py-1.5 bg-transparent text-dark-text-primary placeholder-dark-text-muted border-none focus:outline-none resize-none max-h-[200px] overflow-y-auto text-[15px] leading-relaxed"
                rows={1}
                maxLength={MESSAGE_MAX_LENGTH}
                disabled={sendMessageMutation.isPending}
              />

              {/* Mention Autocomplete */}
              {showMentionAutocomplete && (
                <MentionAutocomplete
                  users={availableUsers}
                  query={mentionQuery}
                  onSelect={handleMentionSelect}
                  onClose={handleMentionClose}
                  position={{ top: -220, left: 0 }}
                  channelContext={!!activeChannelId}
                />
              )}

              {/* Channel Reference Autocomplete */}
              {showChannelAutocomplete && (
                <ChannelMentionAutocomplete
                  channels={referenceableChannels}
                  query={channelQuery}
                  onSelect={handleChannelSelect}
                  onClose={handleChannelClose}
                  position={{ top: -220, left: 0 }}
                />
              )}

              {/* Slash Command Autocomplete */}
              {showSlashAutocomplete && availableCommands.length > 0 && (
                <SlashCommandAutocomplete
                  commands={availableCommands}
                  query={slashQuery}
                  onSelect={handleSlashSelect}
                  onClose={handleSlashClose}
                  position={{ top: -220, left: 0 }}
                />
              )}

              {/* Character count (show when approaching limit) */}
              {content.length > 4500 && (
                <div className="absolute bottom-1 right-2 text-[11px] text-dark-text-muted">
                  {content.length}/{MESSAGE_MAX_LENGTH}
                </div>
              )}
            </div>

            {/* Right side buttons */}
            <div className="flex items-center px-2 py-2 gap-0.5">
              {/* GIF button */}
              <button
                ref={gifButtonRef}
                type="button"
                onClick={() => setShowGifPicker(!showGifPicker)}
                disabled={sendMessageMutation.isPending}
                className={`w-8 h-8 flex items-center justify-center rounded transition-colors disabled:opacity-50 ${
                  showGifPicker
                    ? 'text-primary-500 bg-primary-500/20'
                    : 'text-dark-text-secondary hover:text-dark-text-primary'
                }`}
                title="GIF"
              >
                <span className="text-xs font-bold">GIF</span>
              </button>
              {showGifPicker && (
                <GifPicker
                  anchorRef={gifButtonRef}
                  onGifSelect={handleGifSelect}
                  onClose={() => setShowGifPicker(false)}
                />
              )}

              {/* Poll button */}
              <button
                type="button"
                onClick={handleCreatePoll}
                disabled={sendMessageMutation.isPending}
                className="w-8 h-8 flex items-center justify-center rounded text-dark-text-secondary hover:text-dark-text-primary transition-colors disabled:opacity-50"
                title="Анкета"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </button>

              {/* Voice message button */}
              <button
                type="button"
                onClick={async () => {
                  try {
                    // Pre-acquire mic before showing recording UI — eliminates ~1s warmup delay
                    const stream = await navigator.mediaDevices.getUserMedia({
                      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
                    });
                    preAcquiredStreamRef.current = stream;
                    setIsRecordingVoice(true);
                  } catch (err) {
                    if (err instanceof Error && err.name === 'NotAllowedError') {
                      toast.error('Разрешете достъп до микрофона за да записвате гласови съобщения');
                    } else if (err instanceof Error && err.name === 'NotFoundError') {
                      toast.error('Не е намерен микрофон');
                    } else {
                      toast.error('Грешка при стартиране на записа');
                    }
                  }
                }}
                disabled={sendMessageMutation.isPending}
                className="w-8 h-8 flex items-center justify-center rounded text-dark-text-secondary hover:text-red-500 transition-colors disabled:opacity-50"
                title="Гласово съобщение"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>

              {/* Schedule button */}
              <button
                type="button"
                onClick={() => {
                  if (content.trim()) {
                    setShowScheduleModal(true);
                  } else {
                    toast.error('Въведете съобщение преди планиране');
                  }
                }}
                disabled={sendMessageMutation.isPending}
                className="w-8 h-8 flex items-center justify-center rounded text-dark-text-secondary hover:text-purple-500 transition-colors disabled:opacity-50"
                title="Планирай"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>

              {/* Scheduled list button */}
              <button
                type="button"
                onClick={() => setShowScheduledList(true)}
                disabled={sendMessageMutation.isPending}
                className="w-8 h-8 flex items-center justify-center rounded text-dark-text-secondary hover:text-primary-500 transition-colors disabled:opacity-50"
                title="Планирани"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </button>

              {/* Emoji button */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  disabled={sendMessageMutation.isPending}
                  className={`w-8 h-8 flex items-center justify-center rounded transition-colors disabled:opacity-50 ${
                    showEmojiPicker
                      ? 'text-accent-500 bg-accent-500/20'
                      : 'text-dark-text-secondary hover:text-accent-500'
                  }`}
                  title="Емоджи"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                {showEmojiPicker && (
                  <EmojiPicker
                    placement="right"
                    onEmojiSelect={insertEmoji}
                    onClose={() => setShowEmojiPicker(false)}
                  />
                )}
              </div>

              {/* Send button - only show when there's content */}
              {canSend && (
                <button
                  type="submit"
                  disabled={sendMessageMutation.isPending}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 hover:bg-primary-600 text-white transition-colors disabled:opacity-50 ml-1"
                  title="Изпрати (Enter)"
                >
                  {sendMessageMutation.isPending ? (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </form>

      {/* Poll Creation Modal */}
      {showPollModal && pendingPollMessageId && (
        <PollCreationModal
          messageId={pendingPollMessageId}
          onClose={handlePollCancelled}
          onSuccess={handlePollCreated}
        />
      )}

      {/* Schedule Message Modal */}
      {showScheduleModal && (
        <ScheduleMessageModal
          onClose={() => setShowScheduleModal(false)}
          content={content}
          channelId={activeChannelId}
          dmThreadId={activeDMThreadId}
          onScheduled={() => {
            setContent('');
            if (conversationId) clearDraft(conversationId);
            toast.success('Съобщението е планирано успешно');
          }}
        />
      )}

      {/* Scheduled Messages List Modal */}
      {showScheduledList && (
        <ScheduledMessagesList onClose={() => setShowScheduledList(false)} />
      )}
    </div>
  );
};

export default MessageInput;
